import "./style.scss";

import { AddCircleOutline } from "@mui/icons-material";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ChannelIcon from "components/shared/ChannelIcon";
import EmptyState from "components/shared/EmptyState";
import InformationTitle from "components/shared/InformationTitle";
import StyledButton from "components/shared/StyledButton";
import type { TStartingChannelsData } from "components/TitleFormContainer/types";
import type { ITitle } from "containers/Titles/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";

type TProps = {
    title: ITitle;
    navigate: () => void;
};

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "10px 20px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
    minWidth: "140px",
};

const hasAnyOpeningCumulative = (data: {
    quantity?: string | number | null;
    turnover?: string | number | null;
    turnover_1?: string | number | null;
    turnover_2?: string | number | null;
    cumulativeSales?: string | number | null;
}) =>
    [data.quantity, data.turnover, data.turnover_1, data.turnover_2, data.cumulativeSales].some(
        (value) => value != null && +value !== 0,
    );

const formatQuantity = (value: string | number | null | undefined) => {
    if (value == null || +value === 0) return "—";
    return String(+value);
};

const formatMoney = (value: string | number | null | undefined) => {
    if (value == null || +value === 0) return "—";
    return String(Math.round(+value) / 100);
};

const DataAtCreation = ({ title, navigate }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { labels, labelsStatus } = useAppSelector((state) => state.calculationBaseColumnLabels);

    const { getCompanySettings } = useCompanySettings();
    const usesStocks = getCompanySettings("uses_stock");

    useEffect(() => {
        if (labelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [labelsStatus, dispatch]);

    const labelByColumn = useMemo(
        () => Object.fromEntries(labels.map((label) => [label.calculationBaseColumn, label.label])),
        [labels],
    );

    const quantityLabel = labelByColumn["quantity"] ?? t("CalculationBaseColumnLabels.Columns.quantity");
    const turnoverLabel = labelByColumn["turnover"] ?? t("CalculationBaseColumnLabels.Columns.turnover");
    const customTurnoverColumns = (["turnover_1", "turnover_2"] as const).flatMap((column) =>
        labelByColumn[column] ? [{ column, label: labelByColumn[column] as string }] : [],
    );

    const displayedStartingChannelsData = title.hasArchive
        ? title.startingChannelsData
        : title.startingChannelsData.filter((data) => hasAnyOpeningCumulative(data));

    return (
        <>
            <div className="tab-other-quantity-wrapper">
                {usesStocks && (
                    <span className="tab-other-quantity" style={{ margin: 18, fontSize: 13, color: "#6f6f6f" }}>
                        * {t("NewTitle.QuantityStock")} : {title.initialInventory ?? "—"}
                    </span>
                )}
                {title.hasArchive && (
                    <span className="tab-other-quantity-info">{t("Titles.HasArchivesNonEditableData")}</span>
                )}
            </div>
            <Paper
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    border: `1px solid ${variables["border-grey"]}`,
                    borderRadius: variables["border-raduis"],
                    boxShadow: "none",
                    marginTop: "16px",
                }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tHeadStyles}>{t("Titles.Channels")}</TableCell>
                                <TableCell sx={{ ...tHeadStyles, textAlign: "center" }}>{quantityLabel}</TableCell>
                                <TableCell sx={{ ...tHeadStyles, textAlign: "center" }}>{turnoverLabel}</TableCell>
                                {customTurnoverColumns.map(({ column, label }) => (
                                    <TableCell key={column} sx={{ ...tHeadStyles, textAlign: "center" }}>
                                        {label}
                                    </TableCell>
                                ))}
                                <TableCell sx={tHeadStyles}>
                                    {!title.hasArchive && <InformationTitle onClick={navigate} title={""} />}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="table-start-channel-data">
                            {displayedStartingChannelsData.map(
                                (elm: Omit<TStartingChannelsData, "usesAlternativeMilestoneAmount">, i) => (
                                    <TableRow key={"cumulativeSales" + i}>
                                        <TableCell sx={{ display: "flex", alignItems: "center" }}>
                                            <ChannelIcon
                                                channelKind={elm.kind as number}
                                                sx={{ minHeight: "20px", minWidth: "20px" }}
                                            />
                                            <div style={{ marginLeft: "8px" }} className={`cumulativeSales-${i}-name`}>
                                                {elm.name}
                                            </div>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <span className={`cumulativeSales-${i}-quantity`}>
                                                {formatQuantity(elm.quantity)}
                                            </span>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <span className={`cumulativeSales-${i}-turnover`}>
                                                {formatMoney(elm.turnover)}
                                            </span>
                                        </TableCell>
                                        {customTurnoverColumns.map(({ column }) => (
                                            <TableCell key={column} sx={{ textAlign: "center" }}>
                                                {formatMoney(elm[column])}
                                            </TableCell>
                                        ))}
                                        <TableCell></TableCell>
                                    </TableRow>
                                ),
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <div className="cumulative-sales-empty-state">
                {!displayedStartingChannelsData.length && (
                    <>
                        <EmptyState type="noCumulativeSales" />
                        <StyledButton buttonType="secondary" onClick={navigate}>
                            <AddCircleOutline style={{ height: "16px", width: "16px" }} />
                            {t("Titles.AddData")}
                        </StyledButton>
                    </>
                )}
            </div>
        </>
    );
};

export default DataAtCreation;
